import {useEffect, useState} from "react";
import {useForm, Controller} from "react-hook-form";
import {App as AntdApp, Table, Tag, Space, Button as AntdButton, Popconfirm, Row, Col, Badge} from "antd";
import {Loading, FontAwesomeIcon, Button, ModalForm} from "~/components";
import {InputField, NumberField, SelectField} from "~/components/Forms";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {
	useGetAdminExtraPacksQuery, useCreateExtraPackMutation, useUpdateExtraPackMutation, useDeleteExtraPackMutation,
} from "~/reduxs/api/planApiSlice";
import {nf} from "../util";

/** Loại hạn mức hỗ trợ Gói Extra — khớp PlanLimit::EXTRA_METRICS ở BE. */
const METRIC_OPTIONS = [
	{value: 'auto_content_post', label: 'Bài viết AI tự động'},
];

const EMPTY_VALUES = {
	name: '', metric: 'auto_content_post', units: 5, price: 0,
	description: '', status: 'active', order: 0,
};

/**
 * Tab "Gói Extra" trong màn Quản lý gói (admin) — CRUD bảng extra_packs (sản phẩm
 * mua thêm hạn mức). Xoá bị chặn khi pack còn đơn mua đang chờ duyệt (đơn đã xử lý
 * không chặn nhờ snapshot trong extra_purchases).
 */
function ExtraPackManager() {
	const {notification} = AntdApp.useApp();
	const {data: packs = [], isLoading} = useGetAdminExtraPacksQuery();
	const [createPack, {isLoading: creating}] = useCreateExtraPackMutation();
	const [updatePack, {isLoading: updating}] = useUpdateExtraPackMutation();
	const [deletePack] = useDeleteExtraPackMutation();

	const [open, setOpen] = useState(false);
	const [editing, setEditing] = useState(null); // null = tạo mới; object = sửa

	const {control, handleSubmit, reset} = useForm({defaultValues: EMPTY_VALUES});

	// Nạp lại giá trị mỗi khi mở (tạo mới → mặc định; sửa → giá trị pack).
	useEffect(() => {
		if (!open) return;
		if (editing) {
			reset({
				name: editing.name, metric: editing.metric || 'auto_content_post',
				units: editing.units || 0, price: editing.price || 0,
				description: editing.description || '', status: editing.status || 'active',
				order: editing.order || 0,
			});
		} else {
			reset(EMPTY_VALUES);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, editing]);

	const openCreate = () => { setEditing(null); setOpen(true); };
	const openEdit = (pack) => { setEditing(pack); setOpen(true); };

	const submit = async (values) => {
		const body = {
			name: values.name, metric: values.metric, units: values.units || 0,
			price: values.price || 0, description: values.description || '',
			status: values.status, order: values.order || 0,
		};
		try {
			if (editing) {
				await updatePack({id: editing.id, ...body}).unwrap();
				notification.success({message: 'Đã cập nhật gói Extra'});
			} else {
				await createPack(body).unwrap();
				notification.success({message: 'Đã tạo gói Extra'});
			}
			setOpen(false);
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e)});
		}
	};

	const remove = async (pack) => {
		try {
			await deletePack(pack.id).unwrap();
			notification.success({message: 'Đã xóa gói Extra'});
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e)});
		}
	};

	const columns = [
		{
			title: 'Gói Extra', key: 'name',
			render: (_, p) => (
				<span style={{fontWeight: 600}}>
					{p.name}
					{p.pending_count > 0 && (
						<Badge count={p.pending_count} size="small" title="Đơn mua đang chờ duyệt" style={{marginLeft: 8}} />
					)}
				</span>
			),
		},
		{title: 'Loại hạn mức', dataIndex: 'metric_label', key: 'metric', responsive: ['md']},
		{title: 'Cộng thêm', dataIndex: 'units', key: 'units', align: 'right', render: (v) => `+${nf(v)} bài`},
		{title: 'Giá', dataIndex: 'price', key: 'price', align: 'right', render: (v) => `${nf(v)}đ`},
		{
			title: 'Đơn giá/bài', key: 'unit_price', align: 'right', responsive: ['md'],
			render: (_, p) => `${nf(Math.round((p.price || 0) / (p.units || 1)))}đ`,
		},
		{
			title: 'Trạng thái', key: 'status',
			render: (_, p) => <Tag color={p.status === 'active' ? 'green' : 'default'}>{p.status === 'active' ? 'Đang bán' : 'Ẩn'}</Tag>,
		},
		{title: 'Thứ tự', dataIndex: 'order', key: 'order', align: 'center', responsive: ['md']},
		{
			title: '', key: 'action', align: 'right',
			render: (_, p) => (
				<Space>
					<AntdButton size="small" onClick={() => openEdit(p)}>Sửa</AntdButton>
					<Popconfirm title={`Xóa gói Extra "${p.name}"?`} onConfirm={() => remove(p)} okText="Xóa" cancelText="Thôi">
						<AntdButton size="small" danger>Xóa</AntdButton>
					</Popconfirm>
				</Space>
			),
		},
	];

	return (
		<div>
			<div style={{display: 'flex', justifyContent: 'flex-end', marginBottom: 14}}>
				<Button white onClick={openCreate} leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}>
					Thêm gói Extra
				</Button>
			</div>

			{isLoading ? <Loading /> : (
				<Table
					columns={columns}
					dataSource={packs}
					rowKey="id"
					pagination={false}
					scroll={{x: 'max-content'}}
					locale={{emptyText: 'Chưa có gói Extra nào — bấm "Thêm gói Extra" để tạo.'}}
				/>
			)}

			<ModalForm
				open={open}
				icon="fa-light fa-cart-plus"
				title={editing ? `Sửa gói Extra: ${editing.name}` : 'Thêm gói Extra'}
				subtitle="Sản phẩm mua thêm hạn mức — đơn mua chờ admin xác nhận thanh toán rồi duyệt."
				onCancel={() => setOpen(false)}
				onOk={handleSubmit(submit)}
				okText={editing ? 'Lưu' : 'Tạo'}
				loading={creating || updating}
				width={560}
			>
				<Row gutter={16}>
					<Col span={12}>
						<Controller control={control} name="name" render={({field}) => (
							<InputField label="Tên gói" placeholder="vd: Extra 10 bài" {...field} />
						)} />
					</Col>
					<Col span={12}>
						<Controller control={control} name="metric" render={({field}) => (
							<SelectField label="Loại hạn mức" options={METRIC_OPTIONS} {...field} />
						)} />
					</Col>
					<Col span={8}>
						<Controller control={control} name="units" render={({field}) => (
							<NumberField label="Số bài cộng thêm" min={1} {...field} />
						)} />
					</Col>
					<Col span={8}>
						<Controller control={control} name="price" render={({field}) => (
							<NumberField label="Giá (VND)" min={0} step={1000} {...field} />
						)} />
					</Col>
					<Col span={8}>
						<Controller control={control} name="order" render={({field}) => (
							<NumberField label="Thứ tự" min={0} {...field} />
						)} />
					</Col>
					<Col span={16}>
						<Controller control={control} name="description" render={({field}) => (
							<InputField label="Mô tả" placeholder="vd: Cộng thêm 10 bài viết AI, dùng đến hết chu kỳ gói" maxLength={255} {...field} />
						)} />
					</Col>
					<Col span={8}>
						<Controller control={control} name="status" render={({field}) => (
							<SelectField label="Trạng thái" options={[{value: 'active', label: 'Đang bán'}, {value: 'inactive', label: 'Ẩn'}]} {...field} />
						)} />
					</Col>
				</Row>
			</ModalForm>
		</div>
	);
}

export default ExtraPackManager;
